<script setup lang="ts">
import { computed, ref } from 'vue';

import IconCheckCircle from '~/assets/icons/IconCheckCircle.vue';
import IconFileText from '~/assets/icons/IconFileText.vue';
import StatusCard from '~/components/shared/StatusCard.vue';
import Tabs from '~/components/shared/Tabs.vue';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';

import EquipmentDetails from '../../components/EquipmentDetails.vue';
import useHardwareForm from '../../queries/hardware-form/useHardwareForm';
import InstallationAddressContent from '../equipment/InstallationAddressContent.vue';

interface Props {
  financing: FinancingFlowApi.FinancingV2;
}

const props = defineProps<Props>();

const completedAt = computed(() => props.financing.section_statuses.equipment?.completed_at || undefined);
const submittedAt = computed(() => props.financing.section_statuses.equipment?.submitted_at || undefined);

const projectId = computed(() => props.financing.project_id);
const {
  data: hardwareData,
  error: hardwareError,
  isLoading: isLoadingHardware,
} = useHardwareForm(projectId);

const activeTab = ref<'change_request' | 'current' | 'history' | 'installation_address'>('current');

const formatPower = (power: null | number | undefined) => {
  if (power == null) return '-';
  return `${(power / 1000).toFixed(2)} kWp`;
};

const calculatedInvertersPower = computed(() => {
  if (!hardwareData.value?.inverters?.length) return null;
  return hardwareData.value.inverters.reduce(
    (total, inverter) => total + (inverter.total_power || inverter.power * inverter.quantity),
    0,
  );
});

const calculatedModulesPower = computed(() => {
  if (!hardwareData.value?.modules?.length) return null;
  return hardwareData.value.modules.reduce(
    (total, module) => total + (module.total_power || module.power * module.quantity),
    0,
  );
});

const invertersPower = computed(
  () => calculatedInvertersPower.value ?? hardwareData.value?.total_powers?.inverters ?? null,
);
const modulesPower = computed(
  () => calculatedModulesPower.value ?? hardwareData.value?.total_powers?.modules ?? null,
);

const tabs = computed(() => {
  const hasChangeRequest = false;

  return [
    {
      badge: null,
      disabled: false,
      id: 'current' as const,
      label: 'Atual',
      tooltip: null,
    },
    {
      badge: hasChangeRequest ? 'Pendente' : null,
      disabled: false,
      id: 'change_request' as const,
      label: 'Pedido de Troca',
      tooltip: null,
    },
    {
      badge: null,
      disabled: false,
      id: 'history' as const,
      label: 'Histórico de Troca',
      tooltip: null,
    },
    {
      badge: null,
      disabled: false,
      id: 'installation_address' as const,
      label: 'Endereço de Instalação',
      tooltip: null,
    },
  ];
});
</script>

<template>
  <div class="equipment-step">
    <div class="mb-4">
      <div class="row g-3">
        <div class="status-card-col">
          <StatusCard
            title="Submetido em"
            :datetime="submittedAt"
            :icon="IconFileText"
            type="informative"
          />
        </div>

        <div class="status-card-col">
          <StatusCard
            title="Concluído em"
            :datetime="completedAt"
            :icon="IconCheckCircle"
            type="positive"
          />
        </div>
      </div>
    </div>

    <div v-if="hardwareData?.total_powers" class="mt-4">
      <h4 class="section-title">Potências Totais</h4>
      <div class="row g-3">
        <div v-if="hardwareData.total_powers.system != null" class="power-card-col">
          <div class="power-card">
            <span class="label">Sistema</span>
            <span class="value text-primary">
              {{ formatPower(hardwareData.total_powers.system) }}
            </span>
          </div>
        </div>

        <div v-if="invertersPower != null" class="power-card-col">
          <div class="power-card">
            <span class="label">Inversores</span>
            <span class="value text-success">
              {{ formatPower(invertersPower) }}
            </span>
          </div>
        </div>

        <div v-if="modulesPower != null" class="power-card-col">
          <div class="power-card">
            <span class="label">Módulos</span>
            <span class="value text-info">
              {{ formatPower(modulesPower) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hardwareData" class="mt-4">
      <Tabs v-model="activeTab" :tabs="tabs">
        <template #default="{ activeTab: currentTab }">
          <div v-if="currentTab === 'current'">
            <EquipmentDetails :data="hardwareData" />
          </div>

          <div v-else-if="currentTab === 'change_request'">
            <div class="alert alert-info">
              <div class="alert-with-icon">
                <i class="icon ti ti-info-circle" />
                <div class="content">
                  <h5 class="heading">Nenhum pedido de troca pendente</h5>
                  <p class="text">
                    Não há solicitações de troca de equipamentos no momento.
                  </p>
                </div>
              </div>
            </div>
            <!-- TODO: Quando houver pedido de troca, renderizar EquipmentDetails -->
            <!-- <EquipmentDetails :data="hardwareData.change_request" /> -->
          </div>

          <div v-else-if="currentTab === 'history'">
            <div class="alert alert-info">
              <div class="alert-with-icon">
                <i class="icon ti ti-clock-hour-4" />
                <div class="content">
                  <h5 class="heading">Nenhum histórico disponível</h5>
                  <p class="text">
                    Não há registro de trocas de equipamentos para este projeto.
                  </p>
                </div>
              </div>
            </div>
            <!-- TODO: Quando houver histórico, renderizar lista -->
            <!-- <div v-for="(item, index) in hardwareData.history" :key="index" class="mb-4">
              <h5>Troca {{ index + 1 }} - {{ date(item.date).format('DD/MM/YYYY') }}</h5>
              <EquipmentDetails :data="item" />
            </div> -->
          </div>

          <div v-else-if="currentTab === 'installation_address'" class="mt-4">
            <InstallationAddressContent :project-id="projectId" />
          </div>
        </template>
      </Tabs>
    </div>

    <div v-if="isLoadingHardware" class="mt-4 loading-placeholder">
      <div class="placeholder-glow">
        <div class="placeholder col-12 mb-2" />
        <div class="placeholder col-8 mb-2" />
        <div class="placeholder col-6" />
      </div>
    </div>

    <div v-else-if="hardwareError" class="mt-4">
      <div class="alert alert-warning">
        <p class="mb-0">Não foi possível carregar as informações de equipamentos.</p>
      </div>
    </div>

    <div v-else-if="!hardwareData && !isLoadingHardware" class="mt-4">
      <div class="alert alert-info">
        <p class="mb-0">
          <em>O usuário ainda não informou os dados de equipamentos para este projeto.</em>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.equipment-step {
  .badge {
    font-size: 0.75rem;
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .alert-with-icon {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .icon {
      flex-shrink: 0;
      font-size: 1.5rem;
      opacity: 0.85;
    }

    .content {
      flex: 1;

      .heading {
        margin-bottom: 0.25rem;
        font-weight: 600;
      }

      .text {
        margin-bottom: 0;
        font-size: 0.9375rem;
      }
    }
  }

  .status-card-col {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 50%;
    }
  }

  .power-card-col {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 33.33333333%;
    }
  }

  .power-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 0.5rem;
    border: 1px solid #e3e6f0;

    .label {
      font-size: 0.875rem;
      color: #64748b;
      font-weight: 500;
    }

    .value {
      font-size: 1.5rem;
      font-weight: 700;
    }
  }

  .loading-placeholder {
    .placeholder-glow {
      animation-duration: 1.5s;
    }

    .placeholder {
      background-color: #e9ecef;
      border-radius: 0.375rem;
      min-height: 1rem;

      &.col-12 {
        height: 3rem;
      }

      &.col-8 {
        height: 2.5rem;
      }

      &.col-6 {
        height: 2rem;
      }
    }
  }
}
</style>
